import makeRedirectLink from "@client/content/controls/Link/Redirect/container";
import { getEntityRef, getRootContainerProps } from "@client/content/lib/helper";
import FormStyleProvider from "@client/content/Providers/FormStyle/container";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { unwrapResponsePromise } from "@client/lib/api";
import { sort } from "@client/lib/array";
import { useOptionValue } from "@client/lib/hooks";
import { TargetSelector } from "@client/selectors";
import appInfo, { isStaffOrSuperUser } from "@common/appInfo";
import { useI18n } from "@common/hooks/i18n";
import { i18nFormatHtml } from "@common/lib/i18n";
import { toCamelCase } from "@common/lib/misc";
import getOptions from "@common/lib/options";
import { AffiliateDashboard, Welcome } from "@common/modules/affiliate/component";
import { type Affiliate } from "@common/modules/affiliate/types";
import Network from "@common/modules/network/types";
import classNames from "classnames";
import * as React from "react";
import api from "../api";
import { type MemberAffiliateDashboard } from "../types";
import Leaderboard from "./Leaderboard/container";
import NetworkVolumeKpi from "./NetworkVolumeKpi/container";
import Report from "./Report/container";
import SharesKpi from "./SharesKpi/container";

export type MapProps = RootContainerProps & {
  affiliateDate: string | null;
  affiliateLinks: Network.Offers.Api.AffiliateLink[];
  apiInfo: ModuleApiInfo;
  cycleOptions: Network.Cycles.Api.CycleOption[];
  heading: string;
  fieldStyle: React.CSSProperties;
  gap: string;
  kpiOptions: MemberAffiliateDashboard.View.KpiOption[];
  incentives: MemberAffiliateDashboard.View.Incentive[];
  isMobile: boolean;
  isEditMode: boolean;
  offers: Network.Offers.Api.UserOffer[];
  reportOptions: MemberAffiliateDashboard.View.ReportOption[];
  profilePageId: string;
  status: MemberAffiliateDashboard.Status;
  welcome: MemberAffiliateDashboard.View.WelcomeSettings | null;
  widgetStyle: React.CSSProperties;
};

export interface ActionProps {
  join: () => void;
  goToPage: (pageId: string) => void;
  onBackClick: () => void;
  onKpiClick: (id: string) => void;
  onReportClick: (id: string) => void;
}

type WelcomeViewProps = RootContainerProps & {
  submit: () => Promise<any> | void;
  welcome: MemberAffiliateDashboard.View.WelcomeSettings;
};
const WelcomeView = (props: WelcomeViewProps) => {
  const i18n = useI18n();

  return (
    <Welcome
      description={props.welcome.description}
      modal={{
        buttonsAlign: "right",
        containerSelector: ".wsc_viewport",
        onSubmit: props.submit,
        text: (
          <span>
            {i18nFormatHtml(
              props.welcome.hasTerms ? i18n.fe_BecomeAffiliateTermsInfo : i18n.fe_BecomeAffiliateInfo,
              [
                "TERMS_AND_CONDITIONS",
                () => (
                  <TermsRedirectLink id={props.id} source={props.source}>
                    {i18n.termsAndConditions.toLowerCase()}
                  </TermsRedirectLink>
                )
              ],
              ["SUBSCRIPTION", appInfo.system.brandKey]
            )}
          </span>
        )
      }}
      title={props.welcome.title}
      videoSettings={props.welcome}
    />
  );
};

const TermsRedirectLink = makeRedirectLink(TargetSelector.Entity, "welcome");
export default (props: ActionProps & MapProps) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const [activeItem, setActiveItem] = React.useState<{ id: string; type: "incentive" | "report" }>();
  const kpis = React.useMemo(
    (): Affiliate.KpiOptionItem[] =>
      props.kpiOptions.map((x) => ({
        availability: x.availability,
        label: x.label,
        value:
          x.name === "NetworkVolume"
            ? ({ cycleEndDate, currencyCode }) => (
                <NetworkVolumeKpi
                  key={x.value}
                  {...getEntityRef(props)}
                  itemId={x.value}
                  cycleEndDate={cycleEndDate}
                  currencyCode={currencyCode}
                />
              )
            : x.name === "Shares"
            ? ({ cycleEndDate, currencyCode }) => (
                <SharesKpi
                  key={x.value}
                  {...getEntityRef(props)}
                  itemId={x.value}
                  cycleEndDate={cycleEndDate}
                  currencyCode={currencyCode}
                />
              )
            : x.name
      })),
    [props.kpiOptions, props.id]
  );

  const cyclesSorted = React.useMemo(
    () => sort(props.cycleOptions, { key: "value", order: "desc", type: "date" }),
    [props.cycleOptions]
  );

  const [cycleEndDate, setCycleEndDate] = useOptionValue(cyclesSorted);

  const onBackClick = React.useCallback(() => {
    setActiveItem(undefined);
    props.onBackClick();
  }, [props.onBackClick]);

  return (
    <FormStyleProvider {...getEntityRef(props)}>
      {!!props.welcome && !props.affiliateDate && !isStaffOrSuperUser() ? (
        <WelcomeView {...getRootContainerProps(props)} welcome={props.welcome} submit={props.join} />
      ) : (
        <>
          <AffiliateDashboard
            affiliateLinks={props.affiliateLinks}
            api={{
              audit: {
                commission: {
                  getAudit: (data) => unwrapResponsePromise(api.audit.commission.getAudit(props.apiInfo, data)),
                  getHierarchyUserVolume: (data) =>
                    unwrapResponsePromise(api.audit.commission.getHierarchyUserVolume(props.apiInfo, data)),
                  getHierarchyVolumeTierPoints: (data) =>
                    unwrapResponsePromise(api.audit.commission.getHierarchyVolumeTierPoints(props.apiInfo, data)),
                  getVolume: (data) => unwrapResponsePromise(api.audit.commission.getVolume(props.apiInfo, data))
                },
                download: {
                  commissionUserVolume: (data) => api.audit.download.commissionUserVolume(props.apiInfo, data),
                  commissionVolume: (data) => api.audit.download.commissionVolume(props.apiInfo, data),
                  volumeTierPoints: (data) => api.audit.download.volumeTierPoints(props.apiInfo, data)
                },
                options: {
                  getVolumeTypes: options.getGetOptions("mlm", "volumeTypes", { isCurrency: true })
                },
                user: {
                  getVolume: (data) => unwrapResponsePromise(api.audit.user.getVolume(props.apiInfo, data))
                }
              },
              getHistory: (data) => unwrapResponsePromise(api.getHistory(props.apiInfo, data)),
              getLeaderboardItem: (id: string) => unwrapResponsePromise(api.getLeaderboardItem(props.apiInfo, id)),
              getLeaderboardCount: (id: string) => unwrapResponsePromise(api.getLeaderboardCount(props.apiInfo, id)),
              getPayoutCycles: (data) => unwrapResponsePromise(api.getPayoutCycles(props.apiInfo, data)),
              getProfileData: (cycleEndDate) =>
                unwrapResponsePromise(api.getAffiliateDashboard(props.apiInfo, cycleEndDate))
            }}
            className={classNames({ hidden: activeItem })}
            cycleEndDate={cycleEndDate}
            cycleOptions={props.cycleOptions}
            fieldStyle={props.fieldStyle}
            gap={props.gap}
            historyLabelKeyMapper={(x) => toCamelCase(x.replace("historyType_Network", "").replaceAll("_", ""))}
            incentives={props.incentives}
            isMobile={props.isMobile}
            kpiOptions={kpis}
            offers={props.offers}
            offersTitle={i18n.offers}
            onCycleChange={setCycleEndDate}
            onEditProfileClick={() => props.goToPage(props.profilePageId)}
            onIncentiveClick={(id) => setActiveItem({ id, type: "incentive" })}
            onReportClick={(id) => (setActiveItem({ id, type: "report" }), props.onReportClick(id))}
            reportOptions={props.reportOptions}
            heading={props.heading}
            onKpiClick={props.onKpiClick}
            widgetStyle={props.widgetStyle}
          />

          {activeItem?.type === "incentive" && (
            <Leaderboard
              {...getEntityRef(props)}
              cycleEndDate={cycleEndDate}
              incentiveId={activeItem.id}
              onBackClick={onBackClick}
            />
          )}

          {activeItem?.type === "report" && (
            <Report
              {...getEntityRef(props)}
              itemId={activeItem.id}
              cycleEndDate={cycleEndDate}
              onBackClick={onBackClick}
            />
          )}
        </>
      )}
    </FormStyleProvider>
  );
};
